import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useScrollSequence
 *
 * Tracks scroll progress through a sticky scroll-jacking container.
 * Uses cached offsetTop + window.scrollY to avoid getBoundingClientRect
 * layout-reflow on every scroll event (critical for mobile performance).
 */
export function useScrollSequence(containerRef, frames = 1) {
  const [progress,   setProgress]   = useState(0);
  const [frameIndex, setFrameIndex] = useState(0);

  const rafRef       = useRef(null);
  const offsetTopRef = useRef(0);   // cached container top (px from doc top)
  const seqHRef      = useRef(0);   // cached container height

  // Measure and cache metrics — cheap offsetTop walk, no reflow under scroll
  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    let top  = 0;
    let node = el;
    while (node) { top += node.offsetTop; node = node.offsetParent; }
    offsetTopRef.current = top;
    seqHRef.current      = el.offsetHeight;
  }, [containerRef]);

  useEffect(() => {
    measure();

    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;

        const vh       = window.innerHeight;
        const scrollY  = window.scrollY;
        const seqH     = seqHRef.current;

        const raw     = (scrollY - offsetTopRef.current) / (seqH - vh);
        const clamped = Math.max(0, Math.min(1, raw));

        setProgress(clamped);
        setFrameIndex(Math.min(frames - 1, Math.floor(clamped * frames)));
      });
    };

    // Re-measure on resize so cached values stay accurate
    const handleResize = () => {
      measure();
      handleScroll();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [containerRef, frames, measure]);

  return { progress, frameIndex };
}

export default useScrollSequence;
